import { useEffect, useState } from 'react'
import type { Model } from '../../../types/mfc'
import { ListType, SearchEvent } from '../../constants/enums'
import { pubSubList, pubSubSearch } from '../../state/pubSub'
import { List, activeLists } from '../../state/state.lists'
import { SearchResultUser, searchList } from '../../state/state.search'
import { USERSSTATE, idToName, idToSlug } from '../../state/state.users'

type ListReturnType<T extends boolean = false> = T extends true
    ? Array<number>
    : Array<Model>

// lists can be accessed before data comes in, so we should provide a safe fallback
export const mapListUser = (id: number) =>
    USERSSTATE[id] ?? ({ id, name: idToName[id], slug: idToSlug[id] } as Model)

const setDefaultList =
    <U extends boolean>(key: ListType, returnIDs: U) =>
    () => {
        const list = activeLists[key]

        if (!list) {
            return []
        }

        const ids = list.ids()

        if (returnIDs) {
            return ids as ListReturnType<U>
        }

        return ids.map(mapListUser) as ListReturnType<U>
    }

export const useList = <T extends ListType, U extends boolean>(
    key: T,
    options: {
        returnIDs?: U
        query?: string
        passive?: boolean
        singleUse?: boolean
    } = {},
) => {
    const { returnIDs, singleUse, query, passive } = options
    const [searchResults, setSearchResults] = useState<ListReturnType<U>>(null)
    const [emptyConstant] = useState([])
    const [list, setList] = useState(setDefaultList(key, returnIDs))
    const [isPopulated, setIsPopulated] = useState(!singleUse)

    useEffect(() => {
        if (!key) return

        // set the list again if the key changes so it's available immediately
        // instead of on next update
        setList(setDefaultList(key, returnIDs))

        if (passive || (singleUse && isPopulated)) {
            return
        }

        const callback = (list: List) => {
            // we probably need to ensure the first update is always set
            // so we dont have a case where the list would never load if useList
            // was never called before passiveMode was enabled.
            if (!list) return
            const ids = list.ids()

            if (ids.length > 0) {
                setIsPopulated(true)
            }

            if (returnIDs) {
                setList(ids as ListReturnType<U>)
                return
            }

            const users = ids.map(mapListUser) as ListReturnType<U>
            setList(users)
        }

        return pubSubList.on(key, callback)
    }, [key, passive, singleUse, isPopulated])

    useEffect(() => {
        // set the list again if the key changes so it's available immediately
        // instead of on next update
        if (key) {
            // this is to ensure we trigger setList once for sure even when
            // passive is enabled
            setList(setDefaultList(key, returnIDs))
        }
    }, [])
    useEffect(() => {
        if (!query) {
            setSearchResults(null)
            return
        }

        const callback = (results: Array<SearchResultUser>) => {
            const ids: Array<number> = results.map(({ entry: { id } }) => id)

            if (returnIDs) {
                setSearchResults(ids as ListReturnType<U>)
                return
            }

            const users = ids.map(mapListUser) as ListReturnType<U>
            setSearchResults(users)
        }

        const pubSubDestroy = pubSubSearch.on(
            `${SearchEvent.RESULTS_LIST}::${key}::${query}`,
            callback,
        )

        searchList({ fields: { name: true }, list: key, query })

        return pubSubDestroy
    }, [key, query])

    return searchResults || (list?.length ? list : emptyConstant)
}
